import { push } from 'connected-react-router';
import { setCheckout, setSomethingChanged } from 'containers/CheckoutPage/actions';
import { isEqualCartItems } from 'containers/CheckoutPage/saga';
import { selectCheckout, selectItemDiscount, selectOrderDraftToken } from 'containers/CheckoutPage/selectors';
import { computeCheckoutQuantityAndTotal } from 'containers/CheckoutStep1Page/saga';
import { getDeliveryFee } from 'containers/MainLayout/actions';
import moment from 'moment';
import { call, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  createDeliveryNote,
  getDeliveryNotes,
  getOrderDraft,
  setDraftOrderDeliveryDateTimeApi,
  setDraftOrderDeliveryNoteApi,
} from 'utils/apollo';
import { selectCart } from '../MainLayout/selectors';
import { getOrderDraft as getOrderDraftActions, refreshOrderDraft, updateDeliveryNotes } from './actions';
import ActionTypes from './constants';
import { message } from 'antd';
import translations from 'translations';
import utilMessages from 'utils/messages';
import { showToast } from 'components/ToastNotification';

function* initCheckoutStep3Flow() {
  const token = yield select(selectOrderDraftToken());
  yield put(getOrderDraftActions.request());

  const response = yield call(getOrderDraft, {
    token: token,
  });
  const cart = yield select(selectCart());

  if (!response || response.errors || !response.orderDrafts?.length) {
    yield put(getOrderDraftActions.failure(response.errors));
    yield put(push('/cart'));
  } else {
    if (!!cart?.cartSet[0] && !isEqualCartItems(cart?.cartSet[0]?.items, response.orderDrafts?.[0]?.items)) {
      yield put(setSomethingChanged());
      return;
    }

    // update checkout information
    const checkoutDeliveryInformation = {
      timeSlot: {},
      deliveryDate: {},
      deliveryNotes: [],
      selectedDeliveryNotes: {},
    };

    for (const element of response.orderDrafts) {
      const selectedDeliveryStart = moment(element.deliveryDatetime.start);
      const selectedDeliveryEnd = moment(element.deliveryDatetime.end);
      if (moment().isBefore(selectedDeliveryStart)) {
        checkoutDeliveryInformation.timeSlot[element.supplier.id] = `${selectedDeliveryStart.format(
          'HH:mm',
        )} - ${selectedDeliveryEnd.format('HH:mm')}`;
      }
      checkoutDeliveryInformation.deliveryDate[element.supplier.id] = selectedDeliveryStart.format('DD/MM/YYYY');
      checkoutDeliveryInformation.selectedDeliveryNotes[element.supplier.id] = element.deliveryNotes;
    }

    const deliveryNotesResponse = yield call(getDeliveryNotes);
    if (!deliveryNotesResponse.errors) {
      checkoutDeliveryInformation.deliveryNotes = deliveryNotesResponse;
    }

    const itemDiscount = yield select(selectItemDiscount());
    const checkoutQuantityAndTotalResult = computeCheckoutQuantityAndTotal(response.orderDrafts, itemDiscount);

    yield put(
      setCheckout({
        ...checkoutDeliveryInformation,
        ...checkoutQuantityAndTotalResult,
      }),
    );

    yield put(
      getOrderDraftActions.success({
        orderDrafts: response.orderDrafts,
        store: response.store,
        draftToken: token,
      }),
    );
  }
}

function* updateDeliveryDateTime(action) {
  const { payload } = action;

  const checkoutDeliveryInformation = {
    timeSlot: {},
    deliveryDate: {},
  };
  checkoutDeliveryInformation.deliveryDate[payload.supplierId] = moment(payload.end).format('DD/MM/YYYY');
  checkoutDeliveryInformation.timeSlot[payload.supplierId] = `${moment(payload.start).format('HH:mm')} - ${moment(
    payload.end,
  ).format('HH:mm')}`;

  yield put(setCheckout(checkoutDeliveryInformation));

  const response = yield call(setDraftOrderDeliveryDateTimeApi, {
    input: {
      draftOrderId: payload.draftOrderId,
      start: payload.start,
      end: payload.end,
    },
  });
  if (response.errors) {
    message.error(translations(response.errors[0].message));
  }
  yield put(refreshOrderDraft());
}

function* updateDeliveryNoteFlow(action) {
  const { payload } = action;
  yield put(
    setCheckout({
      selectedDeliveryNotes: {
        [payload.supplierId]: payload.notes,
      },
    }),
  );

  const response = yield call(setDraftOrderDeliveryNoteApi, {
    input: {
      draftOrderId: payload.draftOrderId,
      notes: payload.notes,
    },
  });

  showToast({ message: translations(utilMessages.addDeliveryNoteSuccess) });

  if (response.errors) {
    message.error(translations(response.errors[0].message));
    yield put(refreshOrderDraft());
  }
}

function* addNoteFlow(action) {
  const { payload } = action;

  const checkout = yield select(selectCheckout());
  const response = yield call(createDeliveryNote, {
    text: payload.note,
  });

  if (!response.errors) {
    const deliveryNotesResponse = yield call(getDeliveryNotes);
    if (!deliveryNotesResponse.errors) {
      yield put(setCheckout({ deliveryNotes: deliveryNotesResponse }));
    }
    yield put(
      updateDeliveryNotes({
        draftOrderId: payload.draftOrderId,
        supplierId: payload.supplierId,
        notes: [...(checkout.selectedDeliveryNotes[payload.supplierId] || []), payload.note],
      }),
    );
    showToast({ message: translations(utilMessages.addDeliveryNoteSuccess) });
  } else {
    message.error(response.errors[0].message);
    yield put(refreshOrderDraft());
  }
}

function* getDeliveryFeeFlow() {
  while (true) {
    yield take(ActionTypes.GET_ORDER_DRAFT_SUCCESS);
    const checkout = yield select(selectCheckout());
    yield put(getDeliveryFee.request({ totalPrice: checkout?.total }));
  }
}

export default function* checkoutStep3PageSaga() {
  yield fork(initCheckoutStep3Flow);
  yield fork(getDeliveryFeeFlow);
  yield takeEvery(ActionTypes.UPDATE_DELIVERY_DATE_TIME, updateDeliveryDateTime);
  yield takeEvery(ActionTypes.UPDATE_DELIVERY_NOTES, updateDeliveryNoteFlow);
  yield takeLatest(ActionTypes.ADD_NOTE_SUBMIT, addNoteFlow);
  yield takeLatest(ActionTypes.REFRESH_ORDER_DRAFT, initCheckoutStep3Flow);
}
